import React, { useMemo, useCallback, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  TabPanelProps,
  TabProps,
  useToast,
} from '@chakra-ui/core';
import { useTabsWithConfirm } from './useTabsWithConfirm';
import * as Panes from './components';
import { PaneHandle } from './accountParameter.types';
import { tabListStyle, componentTabStyle, componentTabPanelStyle } from './styles';
import colors from '../../../styles/colors';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { NotAuthorizedView } from '../NotAuthorizedView';
import { useTranslation } from 'react-i18next';

import { SettingsDisabledContext } from './SettingsDisabledContext';

const AppyhereParametersView: React.FC = () => {
  const { t } = useTranslation('accountParameter');
  const [dirtyPanes, setDirtyPanes] = useState<Record<string, boolean>>({});
  const toast = useToast();
  const { updateAccountConfiguration } = useStoreActions((actions) => actions.accountLevelConfiguration);
  const accountId = useStoreState((state) => state.app.accounts?.id);

  const appUserId = useStoreState((s) => s.app.user?.id);
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);
  const isAuthorizationStateLoaded = Boolean(appOwnersId && appUserId);
  const currentUser = useStoreState((state) => state.app.user);

  const isOperatorEnabled = useStoreState((s) => s.app.user?.operator?.enabled);

  const isAppyHereAdmin = Boolean(currentUser?.isAppyHereAdmin);

  const PANE_CONFIG = [
    {
      key: 'interviews',
      title: t('accountParametersView.interviews'),
      description: t('accountParametersView.configureInterviewLimits'),
      component: Panes.InterviewsPane,
    },
    {
      key: 'candidate',
      title: t('accountParametersView.candidate'),
      description: t('accountParametersView.configureCandidateSettings'),
      component: Panes.CandidatePane,
    },
    {
      key: 'requisition',
      title: t('accountParametersView.requisitions'),
      description: t('accountParametersView.configureRequisitionSettings'),
      component: Panes.RequisitionPane,
    },
    {
      key: 'positionMining',
      title: t('accountParametersView.positionMining'),
      description: t('accountParametersView.configurePositionMiningSettings'),
      component: Panes.PositionMiningPane,
    },
    {
      key: 'offer',
      title: t('accountParametersView.offer'),
      description: t('accountParametersView.configureOfferSettings'),
      component: Panes.OfferPane,
    },
    {
      key: 'integrations',
      title: t('accountParametersView.integrations'),
      description: t('accountParametersView.configureThirdPartyIntegrations'),
      component: Panes.IntegrationsPane,
    },
    {
      key: 'analytics',
      title: t('accountParametersView.analytics'),
      description: t('accountParametersView.configureAnalyticsSettings'),
      component: Panes.AnalyticsPane,
    },
  ];

  const PANE_KEYS = PANE_CONFIG.map((p) => p.key);

  const paneRefs = useMemo(
    () =>
      PANE_KEYS.reduce((acc, key) => {
        acc[key] = React.createRef<PaneHandle>();
        return acc;
      }, {} as Record<string, React.RefObject<PaneHandle>>),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setPaneDirty = useCallback((pane: string, isPaneDirty: boolean) => {
    setDirtyPanes((prev) => {
      if (prev[pane] === isPaneDirty) {
        return prev;
      }
      return { ...prev, [pane]: isPaneDirty };
    });
  }, []);

  const handleLeaveDirtyTab = useCallback(
    (fromIndex: number) => {
      const paneKey = PANE_KEYS[fromIndex];
      paneRefs[paneKey]?.current?.reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paneRefs],
  );

  const { tabIndex, handleTabChange, confirmModal } = useTabsWithConfirm(handleLeaveDirtyTab);

  const activePaneKey = PANE_KEYS[tabIndex];
  const isCurrentTabDirty = !!dirtyPanes[activePaneKey];

  const handleSave = useCallback(
    async (pane: string, updates: Record<string, any>): Promise<boolean> => {
      if (!accountId) {
        return false;
      }
      try {
        await updateAccountConfiguration({ configuration: updates, accountId });
        setDirtyPanes((prev) => ({ ...prev, [pane]: false }));
        toast({
          title: t('accountParametersView.success'),
          description: t('accountParametersView.accountParametersUpdatedSuccessfully'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        return true;
      } catch (error) {
        toast({
          title: t('accountParametersView.error'),
          description: t('accountParametersView.failedToUpdateAccountParameters'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId, updateAccountConfiguration, toast],
  );

  const handleGlobalAction = useCallback(
    (action: 'save' | 'reset') => {
      const activePaneRef = paneRefs[activePaneKey];
      activePaneRef?.current?.[action]();
    },
    [paneRefs, activePaneKey],
  );

  const canEdit = isAppyHereAdmin;
  const canView = isOperatorEnabled || canEdit;

  return (
    <Box color={colors.neutralGray[700]} minHeight="100%">
      {canView ? (
        <SettingsDisabledContext.Provider value={!canEdit}>
          <Tabs index={tabIndex} onChange={(index) => handleTabChange(index, isCurrentTabDirty)} minHeight="100%">
            <TabList {...tabListStyle}>
              {PANE_CONFIG.map((pane) => (
                <ComponentTab key={pane.key}>{pane.title}</ComponentTab>
              ))}
            </TabList>

            <Box mt={4} minHeight="100%">
              <Box bg="white" px="1.5rem" py="1rem" borderBottom="0.5px solid #ECEFF1">
                <Flex justify="space-between" align="center">
                  <Box>
                    <Heading as="h4" fontSize="lg" lineHeight="1.3">
                      {t('accountParametersView.appyhereParameters')}
                    </Heading>
                    <Text m="3px 0" color="gray.500" fontSize="12px">
                      {t('accountParametersView.manageAndConfigureAccountLevelSystemParameters')}
                    </Text>
                  </Box>
                  {isCurrentTabDirty && canEdit && (
                    <Flex>
                      {/* <Button variant="ghost" mr={3} onClick={() => handleGlobalAction('reset')}>
                        {t('accountParametersView.reset')}
                      </Button> */}
                      <Button colorScheme="blue" onClick={() => handleGlobalAction('save')}>
                        {t('accountParametersView.saveChanges')}
                      </Button>
                    </Flex>
                  )}
                </Flex>
              </Box>

              <TabPanels minHeight="100%">
                {PANE_CONFIG.map(({ key, description, component: PaneComponent }) => (
                  <ComponentTabPanel key={key}>
                    <Box p={4}>
                      <Text fontSize="sm" mb={4}>
                        {description}
                      </Text>
                      <PaneComponent
                        ref={paneRefs[key]}
                        onSave={(updates: any) => handleSave(key, updates)}
                        setDirty={(isDirty: boolean) => setPaneDirty(key, isDirty)}
                        showActions={false}
                      />
                    </Box>
                  </ComponentTabPanel>
                ))}
              </TabPanels>
            </Box>
          </Tabs>
          <Modal isOpen={confirmModal.isOpen} onClose={confirmModal.onCancel}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('accountParametersView.unsavedChanges')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text>{t('accountParametersView.youHaveUnsavedChangesAreYouSureYouWantToLeave')}</Text>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={confirmModal.onCancel}>
                  {t('accountParametersView.cancel')}
                </Button>
                <Button colorScheme="blue" onClick={confirmModal.onConfirm}>
                  {t('accountParametersView.leave')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </SettingsDisabledContext.Provider>
      ) : (
        <NotAuthorizedView isAuthorizationStateLoaded={isAuthorizationStateLoaded} />
      )}
    </Box>
  );
};

export default AppyhereParametersView;

const ComponentTab: React.FC<TabProps> = ({ children, ...props }) => (
  <Tab {...componentTabStyle} {...props}>
    <Box as="span" visibility="hidden" fontWeight={700} display="block" px="16px" py="12px" whiteSpace="normal">
      {children}
    </Box>
    <Box
      as="span"
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px="16px"
      py="12px"
      whiteSpace="normal"
    >
      {children}
    </Box>
  </Tab>
);

const ComponentTabPanel: React.FC<TabPanelProps> = (props) => <TabPanel {...componentTabPanelStyle} {...props} />;
